import { useId, useState } from "react";
import type { ReactNode } from "react";

// A destructive bulk action behind a TYPED confirmation.
//
// For an action that erases a stored decision permanently, a plain button is one stray
// click from a loss that cannot be undone from the UI: "Forget all remembered matches"
// discards every pairing finance has built up for an insurer over months, and there is no
// restore. A second click alone is not enough either — a mis-click is usually followed by
// another one on the same spot. Typing the word forces the hand off the mouse and puts
// the scope of the loss (how many, for whom) in front of the user while they do it.
//
// Presentational only: it decides nothing, and calls onConfirm exactly once per confirmed
// action. Escape or Cancel closes it with nothing done.
export default function ConfirmDangerAction({
  label, title, body, confirmWord, confirmLabel, disabled, onConfirm,
}: {
  label: string;
  title: string;
  body: ReactNode;
  // The word the user must type. Compared case-insensitively after trimming.
  confirmWord: string;
  confirmLabel: string;
  disabled?: boolean;
  onConfirm: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const inputId = useId();
  const ready = typed.trim().toUpperCase() === confirmWord.toUpperCase();

  function close() {
    setOpen(false);
    setTyped("");
  }

  function confirm() {
    if (!ready || disabled) return;
    close();
    onConfirm();
  }

  if (!open) {
    return (
      <button type="button" className="btn" disabled={disabled}
        onClick={() => { setTyped(""); setOpen(true); }}>
        {label}
      </button>
    );
  }

  return (
    <div className="confirm-danger" role="group" aria-label={title}>
      <p className="confirm-danger-title">{title}</p>
      <p className="muted confirm-danger-body">{body}</p>
      <div className="confirm-danger-row">
        <label className="confirm-danger-label" htmlFor={inputId}>
          Type <strong>{confirmWord}</strong> to confirm
        </label>
        <input id={inputId} className="input confirm-danger-input" value={typed}
          autoFocus autoComplete="off" spellCheck={false}
          placeholder={confirmWord}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); confirm(); }
            if (e.key === "Escape") { e.preventDefault(); close(); }
          }} />
        <button type="button" className="btn" onClick={close}>Cancel</button>
        <button type="button" className="btn btn-danger" disabled={disabled || !ready}
          onClick={confirm}>
          {confirmLabel}
        </button>
      </div>
    </div>
  );
}
